import {createSlice, isAnyOf} from "@reduxjs/toolkit";
import {ACCOUNT_STATUS_INITIAL, ACCOUNT_STATUS_LOADED, ACCOUNT_STATUS_LOADING} from "~/constants/Account";
import type {AccountInitialState} from "~/reducers/account.types";
import {enhancedAuthAPI} from "~/api/enhancedAuthAPI";
import {enhancedUserAPI} from "~/api/enhancedUserAPI";
import type {AuthPostOAuthSignInResponse, AuthPostSignInResponse} from "~/api/authAPI.types";
import {UsercommonStatus} from "~/api/authAPI.types";

const initialState: AccountInitialState = {
    id: 0,
    state: null,
    status: null,
    username: undefined,
    "api-token": null,
    "jwt-token": null,
    "restricted-pages": [],
};

const anonymousState: AccountInitialState = {
    ...initialState,
    status: UsercommonStatus.Anonymous,
    username: UsercommonStatus.Anonymous,
};

function signInMatcher(state: AccountInitialState, auth: AuthPostSignInResponse | AuthPostOAuthSignInResponse): AccountInitialState {
    return auth["user-id"] && auth["jwt-token"]?.token
        ? {
            ...initialState,
            state: state.state,
            status: auth["status"] || UsercommonStatus.Active,
            id: auth["user-id"],
            "jwt-token": auth["jwt-token"],
            "api-token": auth["api-token"],
            email: auth.email,
            username: auth.email,
        } as AccountInitialState
        : state;
}

export const accountSlice = createSlice({
    name: "account",
    initialState,
    reducers: {
        init: (state) => ({
            ...state,
            state: ACCOUNT_STATUS_INITIAL,
        }),
    },
    extraReducers: builder => {
        builder.addMatcher(
            isAnyOf(enhancedUserAPI.endpoints.getUserCurrent.matchPending),
            (state) => {
                return {
                    ...state,
                    state: ACCOUNT_STATUS_LOADING
                };
            }
        );
        builder.addMatcher(
            enhancedUserAPI.endpoints.getUserCurrent.matchFulfilled,
            (state, {payload: user}) => {
                return {
                    ...state,
                    ...(user || {}),
                    state: ACCOUNT_STATUS_LOADED,
                };
            }
        );
        builder.addMatcher(
            enhancedUserAPI.endpoints.getUserCurrent.matchRejected,
            (state, {payload}) => {
                const errorPayload = payload as {status: number};
                return errorPayload && errorPayload.status && errorPayload.status === 401 ? anonymousState : state;
            }
        );
        builder.addMatcher(
            enhancedUserAPI.endpoints.putUserCurrent.matchFulfilled,
            (state, {payload: user}) => {
                return {
                    ...state,
                    ...(user || {}),
                    state: ACCOUNT_STATUS_LOADED,
                };
            }
        );
        builder.addMatcher(
            enhancedAuthAPI.endpoints.postAuthSignIn.matchFulfilled,
            (state, {payload: auth} ) => {
                return signInMatcher(state, auth);
            }
        );
        builder.addMatcher(
            enhancedAuthAPI.endpoints.postAuthOauthSignIn.matchFulfilled,
            (state, {payload: auth} ) => {
                return signInMatcher(state, auth);
            }
        );
        builder.addMatcher(
            enhancedAuthAPI.endpoints.deleteAuthSignOut.matchFulfilled,
            () => anonymousState
        );
    },
    selectors: {
        getAccountStatus: state => state.status,
        getAccountState: state => state.state,
        isAnonymous: state => isStateAnonymous(state),
        getRestrictedPages: state => state["restricted-pages"],
        getAuthToken: state => state["api-token"],
        getJWTToken: state => state["jwt-token"],
        getAccountGreetingName: state => {
            return state["first-name"] || state.email?.substring(0, state.email?.indexOf("@"));
        },
        getCurrentUser: state => isStateAnonymous(state) ? undefined : state,
    }
});

function isStateAnonymous(state: AccountInitialState): boolean {
    return !!state.state && (state.id === 0 || state.status === UsercommonStatus.Anonymous);
}

export const {init} = accountSlice.actions;

export const {
    getAccountState,
    getAccountStatus,
    getAuthToken,
    getJWTToken,
    isAnonymous,
    getCurrentUser,
    getAccountGreetingName,
} = accountSlice.selectors;

export default accountSlice;